import { apiGet, apiPost, apiPut, apiDelete, API_PATH } from "../api/client";
import type { CompanyDto } from "../store/companies.store";

export interface CheckInnRequest {
  inn: string;
  name?: string;
  description?: string;
  address?: string;
  director?: string;
  telephone?: string;
  email?: string;
}

export interface CheckInnResponse {
  exists: boolean;
  companyId: number | null;
  name: string | null;
  status: string | null;
}

export interface CompanyUpdateRequest {
  companyId: number;
  name?: string;
  logo?: string;
  description?: string;
  address?: string;
  director?: string;
  telephone?: string;
  email?: string;
}

// ---------------------------------------------------------------------------
// Нормализация CompanyDto: сервер отдаёт camelCase (adminIds, instanceIds,
// createdAt, updatedAt), а стор ожидает snake_case (admin_ids, instance_ids,
// created_at, updated_at). Маппер гарантирует, что массивы не undefined.
// Обрабатывает оба формата ключей: camelCase (реальный backend) и snake_case
// (тестовые моки) — чтобы prod и test-режим вели себя одинаково.
// ---------------------------------------------------------------------------
const firstDefined = <T>(...values: (T | undefined)[]): T | undefined =>
  values.find((v) => v !== undefined && v !== null) as T | undefined;

const mapCompany = (raw: Record<string, unknown>): CompanyDto => ({
  id: raw.id as number,
  admin_ids: (firstDefined(
    raw.adminIds as number[],
    raw.admin_ids as number[],
  ) ?? []) as number[],
  name: raw.name as string,
  logo: (raw.logo as string | null) ?? null,
  description: (raw.description as string | null) ?? null,
  inn: raw.inn as string,
  address: (raw.address as string | null) ?? null,
  director: (raw.director as string | null) ?? null,
  telephone: (raw.telephone as string | null) ?? null,
  email: (raw.email as string | null) ?? null,
  status: raw.status as CompanyDto["status"],
  instance_ids: (firstDefined(
    raw.instanceIds as number[],
    raw.instance_ids as number[],
  ) ?? []) as number[],
  role: (raw.role as "admin" | "member") ?? "member",
  created_at: (firstDefined(
    raw.createdAt as string,
    raw.created_at as string,
  ) ?? "") as string,
  updated_at: (firstDefined(
    raw.updatedAt as string,
    raw.updated_at as string,
  ) ?? "") as string,
});

export const CompaniesService = {
  async getCompanies(): Promise<CompanyDto[]> {
    const raw = await apiGet<Record<string, unknown>[]>(
      `${API_PATH}/companies`,
    );
    return raw.map(mapCompany);
  },

  async checkInn(request: CheckInnRequest): Promise<CheckInnResponse> {
    return apiPost<CheckInnResponse>(
      `${API_PATH}/companies/check-inn`,
      request,
    );
  },

  async updateCompany(request: CompanyUpdateRequest): Promise<CompanyDto> {
    const raw = await apiPut<Record<string, unknown>>(
      `${API_PATH}/companies`,
      request,
    );
    return mapCompany(raw);
  },

  async approveCompany(companyId: number): Promise<CompanyDto> {
    const raw = await apiPost<Record<string, unknown>>(
      `${API_PATH}/moderation/companies/${companyId}/approve`,
    );
    return mapCompany(raw);
  },

  async leaveCompany(companyId: number): Promise<void> {
    await apiDelete(`${API_PATH}/companies/${companyId}/leave`);
  },
};
